import { api } from './axios'
import type {
  LatestSnapshotReportResponseDto,
  StorageReportResponseDto,
  TenantAnalyticsResponseDto,
} from './types'

export interface StorageReportFilters {
  search?: string
  status?: 'active' | 'suspended' | 'pending_confirmation' | 'incomplete'
  resellerId?: string
  sortBy?: 'tenantName' | 'status' | 'createdAt'
  sortOrder?: 'asc' | 'desc'
  offset?: number
  limit?: number
}

export interface TenantAnalyticsFilters {
  startDate?: string
  endDate?: string
}

/**
 * Get storage report with global aggregates and tenant data
 */
export const getStorageReport = async (
  filters?: StorageReportFilters,
): Promise<StorageReportResponseDto> => {
  const response = await api.get<StorageReportResponseDto>('/reports', {
    params: filters,
  })

  return response.data
}

/**
 * Export storage report as CSV
 */
export const exportStorageReport = async (filters?: StorageReportFilters): Promise<Blob> => {
  const response = await api.get<Blob>('/reports/export', {
    params: filters,
    responseType: 'blob',
  })

  return response.data
}

/**
 * Export storage report as CSV with comprehensive data
 */
export const exportStorageReportCSV = async (filters?: StorageReportFilters): Promise<Blob> => {
  const response = await api.get<Blob>('/reports/export-csv', {
    params: filters,
    responseType: 'blob',
  })

  return response.data
}

/**
 * Get detailed analytics for a specific tenant
 */
export const getTenantAnalytics = async (
  tenantId: string,
  filters?: TenantAnalyticsFilters,
): Promise<TenantAnalyticsResponseDto> => {
  const response = await api.get<TenantAnalyticsResponseDto>(`/reports/${tenantId}`, {
    params: filters,
  })

  return response.data
}

/**
 * Export specific chart data as CSV
 */
export const exportTenantChart = async (
  tenantId: string,
  chartType:
    | 'storage-usage'
    | 'api-usage'
    | 'user-growth'
    | 'bandwidth'
    | 'status-history'
    | 'plan-history'
    | 'all-charts',
  filters?: TenantAnalyticsFilters,
): Promise<Blob> => {
  const response = await api.get<Blob>(`/reports/${tenantId}/export/${chartType}`, {
    params: filters,
    responseType: 'blob',
  })

  return response.data
}

/**
 * Export all charts data as combined CSV
 */
export const exportAllTenantCharts = async (
  tenantId: string,
  filters?: TenantAnalyticsFilters,
): Promise<Blob> => {
  const response = await api.get<Blob>(`/reports/${tenantId}/export/all-charts`, {
    params: filters,
    responseType: 'blob',
  })

  return response.data
}

/**
 * Get latest snapshot date for a tenant
 */
export const getLatestSnapshot = async (
  tenantId: string,
): Promise<LatestSnapshotReportResponseDto> => {
  const response = await api.get<LatestSnapshotReportResponseDto>(
    `/reports/${tenantId}/latest-snapshot`,
  )

  return response.data
}
